import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../design-system/form/form-primitives';
import { Stack } from '../../../design-system/stack/stack';
import { cn } from '../../../utils/cn';

type PaymentInputFieldProps<TFieldValues extends FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  type?: 'text' | 'number';
  label: string;
  inputName: FieldPath<TFieldValues>;
  badge?: string;
  placeholder?: string;
  isOptional?: boolean;
  isDisabled?: boolean;
  className?: string;
};

export const PaymentInputField = <TFieldValues extends FieldValues>({
  form,
  type = 'text',
  label,
  inputName,
  badge,
  placeholder,
  isOptional = false,
  isDisabled = false,
  className,
}: PaymentInputFieldProps<TFieldValues>) => {
  return (
    <FormField
      control={form.control}
      name={inputName}
      render={({ field, formState }) => {
        return (
          <Stack space={1} align="stretch" className="relative">
            {badge && <p className="absolute right-3 top-1 text-[10px] font-medium text-seapoint-gray">{badge}</p>}

            <FormItem
              className={cn(
                'rounded-lg border border-seapoint-gray-border bg-white px-4 py-3 focus-within:outline-none focus-within:ring-2 focus-within:ring-seapoint-yellow focus-within:ring-offset-2 has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-seapoint-gray-background-modal',
                { 'border-seapoint-red': !!formState.errors[inputName] },
                className,
              )}
            >
              <Stack space={1} align="stretch">
                <FormLabel className="text-xs font-medium text-seapoint-gray">
                  {label}
                  {isOptional && <span className="text-inherit"> (Optional)</span>}
                </FormLabel>

                <FormControl>
                  <input
                    type={type}
                    placeholder={placeholder}
                    className={cn(
                      'font-base w-full font-medium text-black placeholder:text-seapoint-gray focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-seapoint-gray-background-modal disabled:placeholder:text-seapoint-gray/80',
                      {
                        '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none':
                          type === 'number',
                      },
                      className,
                    )}
                    aria-required={!isOptional}
                    {...field}
                    // Prevents controlled / uncontrolled console warning message
                    value={field.value ?? ''}
                    disabled={isDisabled || field.disabled}
                  />
                </FormControl>
              </Stack>
            </FormItem>

            <FormMessage className="w-full text-right" />
          </Stack>
        );
      }}
    />
  );
};
